import { useConvexConfig } from "@/hooks/useConvexConfig";
import { useConvexDraw } from "@/hooks/useConvexDraw";
import { useConvexGame } from "@/hooks/useConvexGame";
import { useTouchClick } from "@/hooks/useTouchClick";
import { cn } from "@/lib/utils";
import { useGameStore } from "@/store/gameStore";
import { useConvexConnectionState } from "convex/react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

/** Back, Draw (or Next Round once the deck is empty), and next. */
export const PlayControls = () => {
  useConvexGame(); // Sync game state from Convex into store
  const navigateNext = useGameStore((state) => state.navigateNext);
  const navigateBack = useGameStore((state) => state.navigateBack);
  const localDrawnCards = useGameStore((state) => state.localDrawnCards);
  const drawnIndex = useGameStore((state) => state.drawnIndex);

  const disableBack = localDrawnCards.length === 0 || drawnIndex <= 0;
  const disableNext =
    localDrawnCards.length === 0 || drawnIndex >= localDrawnCards.length - 1;

  return (
    <div className="flex items-center gap-3">
      <ArrowButton
        label="Previous Card"
        disabled={disableBack}
        onAction={navigateBack}
      >
        <ArrowLeft className="size-7" strokeWidth={3} />
      </ArrowButton>
      <DrawButton />
      <ArrowButton
        label="Next Card"
        disabled={disableNext}
        onAction={navigateNext}
      >
        <ArrowRight className="size-7" strokeWidth={3} />
      </ArrowButton>
    </div>
  );
};

const ArrowButton: React.FC<{
  label: string;
  disabled: boolean;
  onAction: () => void;
  children: React.ReactNode;
}> = ({ label, disabled, onAction, children }) => {
  const touchHandlers = useTouchClick({
    onAction,
    preventDoubleTouch: false,
    disabled,
  });

  return (
    // Smoky, so the arrows look the same over any card or suit.
    <button
      type="button"
      className="glass-smoky grid size-[78px] shrink-0 touch-manipulation place-items-center rounded-full text-white transition-[scale,color] duration-150 select-none active:scale-95 disabled:*:opacity-30"
      {...touchHandlers}
      aria-label={label}
      disabled={disabled}
    >
      {children}
    </button>
  );
};

/** The big pill-shaped button for the screen's main action, in liquid glass. */
const pill =
  "@container glass-liquid h-[78px] min-w-0 flex-1 touch-manipulation rounded-full px-3 font-extrabold text-slate-900 uppercase transition-[scale,color] duration-150 select-none active:scale-95 disabled:text-slate-900/50 animate-in fade-in";

/** Opens court setup when there's no game yet. */
export const NewGameButton: React.FC<{ onClick: () => void }> = ({
  onClick,
}) => (
  <button
    type="button"
    className={cn(pill, "w-full [--glass-tint:var(--accent)]")}
    onClick={onClick}
  >
    <span className="text-[28px]">New Game</span>
  </button>
);

/** Draws the next card, or starts the next round once the deck is empty. */
const DrawButton = () => {
  const game = useGameStore((state) => state.game);
  const canPlay = useGameStore((state) => state.canPlay());
  const isComplete = useGameStore((state) => state.isComplete());
  const { drawCard } = useConvexDraw();
  const { configGameWithSync } = useConvexConfig();
  const { isWebSocketConnected } = useConvexConnectionState();
  const [showNextRound, setShowNextRound] = useState(false);
  const [drawDisabled, setDrawDisabled] = useState(false);
  const drawInFlight = useRef(false);
  const drawCooldown = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const isMounted = useRef(true);
  // No draw while a change is on its way, including a first share; a
  // shared game also needs its round and a connection.
  const waitingForSync =
    game.syncStatus === "syncing" ||
    Boolean(game.slug && (!game.roundId || !isWebSocketConnected));
  const drawUnavailable = drawDisabled || waitingForSync;

  // Ignore a second tap that lands while a draw is still on its way or its
  // card is still flying in.
  const handleDraw = async () => {
    if (drawInFlight.current) return;
    drawInFlight.current = true;
    setDrawDisabled(true);

    try {
      await drawCard();
    } finally {
      if (!isMounted.current) {
        drawInFlight.current = false;
      } else {
        drawCooldown.current = setTimeout(() => {
          drawInFlight.current = false;
          setDrawDisabled(false);
        }, 400);
      }
    }
  };

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (drawCooldown.current) clearTimeout(drawCooldown.current);
    };
  }, []);

  const handleNextRound = async () => {
    setShowNextRound(false);
    // Deal the same courts and players again.
    await configGameWithSync({
      courts: game.courts,
      players: game.players,
      perCourt: game.perCourt,
      shortCourt: game.shortCourt,
    });
  };

  const drawTouchHandlers = useTouchClick({
    onAction: handleDraw,
    disabled: drawUnavailable,
    preventDoubleTouch: true,
    doubleTouchDelay: 400,
  });

  const nextRoundTouchHandlers = useTouchClick({
    onAction: handleNextRound,
    disabled: !showNextRound,
  });

  // Wait before offering a new round, so a player still tapping Draw
  // doesn't start one by accident.
  useEffect(() => {
    if (isComplete) {
      const timer = setTimeout(() => setShowNextRound(true), 2000);
      return () => clearTimeout(timer);
    }
    setShowNextRound(false);
  }, [isComplete]);

  if (isComplete) {
    return (
      <button
        type="button"
        key="next-round"
        className={cn(pill, "[--glass-tint:var(--color-cyan-300)]")}
        {...nextRoundTouchHandlers}
        disabled={!showNextRound}
        aria-label="Start next round"
        aria-describedby="round-complete"
      >
        {/* Shrinks to fit the pill, like iOS's minimum scale factor. */}
        <span className="block text-[length:min(28px,13cqw)] whitespace-nowrap">
          Next Round
        </span>
        <span id="round-complete" className="sr-only">
          Round completed. Click to start a new round with the same settings.
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      key="draw"
      className={cn(pill, "[--glass-tint:var(--accent)]")}
      {...drawTouchHandlers}
      disabled={!canPlay || drawUnavailable}
      aria-label={waitingForSync ? "Waiting for game sync" : "Draw next card"}
    >
      <span className="text-[28px]">Draw</span>
    </button>
  );
};
